<template>
  <div class="map-container">
    <div data-cy="map" :class="{'map': true, 'map--marker-labels-visible': showMarkerLabels}" ref="mapRef" :data-zoom="zoomLevel"></div>
    <slot></slot>
    <Teleport v-if="renderInfoWindowComponent" to="#ef-map-info-window-content">
      <MapsPopup
        v-if="selectedCubeName"
        :info-window="infoWindow"
        :cube-name="selectedCubeName"
      />
    </Teleport>
    <AfterTourPopup />
    <NewsletterPopup v-if="isDemoUser" />
    <AfterTourExplanationPopup
      v-if="isDemoUser && tour.isTourComplete.value && afterTourExplanationPopup.isVisible.value"
    />
  </div>
</template>

<script setup>
  import MapsPopup from '@/components/efriends/MapsPopup.vue'
  import AfterTourPopup from '@/components/popups/AfterTourPopup.vue'
  import AfterTourExplanationPopup from '@/components/popups/AfterTourExplanationPopup.vue'
  import NewsletterPopup from '@/components/popups/NewsletterPopup.vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useMap } from '@/composables/useMap.ts'
  import { useMyCube } from '@/composables/useMyCube.js'
  import { toRef } from 'vue'
  import { useSpecificPopup } from '@/composables/useSpecificPopup.ts'
  import { useTour } from '@/composables/useTour.ts'

  const props = defineProps({
    // Note that we retrieve all cubes in addition to the already filtered list.
    // We do this because we keep an array of all marker objects even though
    // only some are visible.
    // According to https://developers.google.com/maps/documentation/javascript/examples/advanced-markers-zoom
    // the way to hide markers is to set their map to null. This way we can
    // reuse all markers and only show/hide what is necessary.
    // This is in contrast to re-computing a new list of markers every time a
    // filter, the map center or map zoom has been changed.
    allCubes: {
      required: true,
      type: Object,
    },
    filteredCubes: {
      required: true,
      type: Array,
    },
    isTour: {
      type: Boolean,
      default: false,
    },
  })
  const router = useRouter()
  const currentRoute = useRoute()
  const { isDemoUser } = useMyCube()
  const allCubes = toRef(() => props.allCubes)
  const filteredCubes = toRef(() => props.filteredCubes)

  const afterTourPopup = useSpecificPopup('afterTour')
  const afterTourExplanationPopup = useSpecificPopup('afterTourExplanation')

  const {
    mapRef,
    mapObject,
    mapInitialized,
    google,
    selectedCubeName,
    infoWindow,
    renderInfoWindowComponent,
    updateMapMarkers,
    zoomToFitResults,
    showMarkerLabels,
    zoomLevel,
  } = useMap(router, allCubes, filteredCubes, props.isTour)

  const tour = useTour(router, mapObject, google, mapInitialized)

  if (props.isTour) {
    tour.initTour()
  }

  // Allow the parent / sidebar component to update the markers when a cube
  // filter is changed by the user.
  defineExpose({
    updateMapMarkers,
    zoomToFitResults,
    tour,
  })
</script>

<style lang="scss">
  $map-dropshadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;

  // These values are copied from the old google maps styles:
  $map-marker-font-family: Roboto, Arial, sans-serif;
  $map-marker-font-size: 14px;

  .map-marker {
    position: relative;
    width: 45px;
    height: 45px;

    // We need to add specicivity to be able to override google's default styles.
    img {
      max-width: 100%;
    }

    &--consumer {
      width: 40px;
      height: 40px;
      transform: translateY(50%);
    }

    &--producer {
      width: 46px;
      height: 46px;
      // Magic numbers to move the bottom point of the marker svg to as close
      // as possible to the coordinates in the map.
      transform: translate(0, 4px);
    }

    &--special {
      width: 60px;
      height: 60px;
    }

    &__label {
      // We position the label absolutely so that its inherently variable length
      // does not mess up the marker's position.
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      font-family: $map-marker-font-family;
      font-size: $map-marker-font-size;
      // Prevent the browser from wrapping the label into more than 1 line.
      white-space: nowrap;
      display: none;

      .map--marker-labels-visible & {
        display: block;
      }
    }
  }

  .map-container {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    transition: $transition-base;
    overflow-x: hidden;

    .map {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      transition: $transition-base;

      @media (min-width: $bp-lg) {
        width: 100% - $map-sidebar-width-lg;
      }
    }

    > .map-filter {
      position: absolute;
      right: 0.8rem;
      top: $spacer10;

      > * {
        box-shadow: $map-dropshadow;
        background-color: $white;
      }

      @media (min-width: $bp-lg) {
        display: none;
      }
    }
  }

  .map-list-button {
    position: absolute;
    bottom: 2.2rem;
    right: 0.8rem;
    box-shadow: $map-dropshadow;

    @media (min-width: $bp-lg) {
      display: none;
    }
  }

</style>
